import React from 'react';
import { motion } from 'framer-motion';
import { Eye, Heart, Plus } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { 
  isPrescriptionRequired, 
  getPrescriptionButtonText, 
  getPrescriptionButtonClasses 
} from '../utils/prescriptionUtils';
import { getProductImage } from '../utils/categoryUtils';

const ProductCard = ({ product, onAddToCart, defaultImage }) => {
  const navigate = useNavigate();
  
  // Mock discount if not present for the "modern" look in reference
  const hasDiscount = product.retailPrice > 2000 || product.id % 3 === 0;
  const discountText = hasDiscount ? (product.id % 2 === 0 ? "40% Off" : "20% Off") : null;
  const oldPrice = hasDiscount ? Math.round(product.retailPrice * (1 + (parseInt(discountText) / 100))) : null;

  return (
    <motion.div
      className="group bg-white rounded-[2rem] shadow-sm hover:shadow-2xl transition-all duration-500 overflow-hidden border border-neutral-100 flex flex-col h-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -8 }}
    >
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden bg-white p-6 flex items-center justify-center">
        <img
          src={getProductImage(product, defaultImage)}
          alt={product.itemName}
          className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-700"
        />
        
        {/* Discount Badge */}
        {discountText && (
          <div className="absolute top-4 left-4">
            <span className="bg-primary-600 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg uppercase tracking-wider shadow-sm">
              {discountText}
            </span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="absolute top-4 right-4 flex flex-col gap-2">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/product-details/${product.id}`);
            }}
            className="p-2 bg-white rounded-full shadow-sm border border-neutral-100 text-neutral-400 hover:text-primary-600 transition-all"
          >
            <Eye size={18} />
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="p-2 bg-white rounded-full shadow-sm border border-neutral-100 text-primary-500 hover:scale-110 transition-all"
          >
            <Heart size={18} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 pb-6 flex flex-col flex-grow">
        <h3 className="text-sm md:text-base font-bold text-neutral-800 mb-4 line-clamp-2 min-h-[3rem]">
          {product.itemName}
        </h3>
        
        <div className="mt-auto">
          <div className="flex flex-col mb-4">
            {oldPrice && (
              <span className="text-xs text-neutral-300 line-through">
                KES {oldPrice.toLocaleString()}
              </span>
            )}
            <span className="text-base md:text-lg font-bold text-neutral-800">
              KES {Number(product.retailPrice).toLocaleString()}
            </span>
          </div>

          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              onAddToCart(product);
            }}
            className="w-full py-3 px-4 rounded-full bg-[#F5F7FF] text-neutral-800 font-bold text-sm flex items-center justify-center gap-2 hover:bg-primary-600 hover:text-white transition-all duration-300"
            whileTap={{ scale: 0.95 }}
          >
            <Plus size={18} />
            <span>{getPrescriptionButtonText(product)}</span>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
